import React, { useEffect, useRef, useState } from "react";
import api from "../../services/api";

interface ClockPunchProps {
  onClose: () => void;
}

interface PunchResult {
  action: "in" | "out";
  employee_name: string;
  hours: number;
}

export default function ClockPunch({ onClose }: ClockPunchProps) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PunchResult | null>(null);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const submitPin = async (pinValue: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/scheduling/clock/punch", {
        pin: pinValue,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setResult(res.data);
      closeTimer.current = setTimeout(onClose, 3000);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || "PIN not recognized");
      setPin("");
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleDigit = (digit: string) => {
    if (loading || result || pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);
    setError("");
    if (newPin.length === 4) submitPin(newPin);
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
    setError("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "Backspace") { handleBackspace(); return; }
    if (/^\d$/.test(e.key)) handleDigit(e.key);
  };

  const now = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(10, 25, 47, 0.95)" }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      ref={(el) => { if (!result) el?.focus(); }}
    >
      <div
        className="rounded-2xl shadow-2xl w-full max-w-xs p-6 text-center"
        style={{ background: "var(--rbw-bg-card)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {result ? (
          <div className="py-4">
            <div
              className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center text-3xl"
              style={{ background: result.action === "in" ? "#D1FAE5" : "#FEF3C7" }}
            >
              {result.action === "in" ? "✓" : "👋"}
            </div>
            <div className="text-xl font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
              {result.employee_name}
            </div>
            <div
              className="text-lg font-bold mb-1"
              style={{ color: result.action === "in" ? "#065F46" : "#92400E" }}
            >
              Clocked {result.action === "in" ? "IN" : "OUT"} at {now}
            </div>
            {result.action === "out" && result.hours > 0 && (
              <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                {result.hours.toFixed(2)} hours this shift
              </div>
            )}
            <button
              onClick={onClose}
              className="mt-4 w-full py-2.5 rounded-xl font-bold text-sm"
              style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
              Time Clock
            </div>
            <div className="text-sm font-semibold mb-3" style={{ color: "var(--rbw-navy)" }}>
              Enter your PIN to clock in or out
            </div>

            <div className="flex justify-center gap-3 mb-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full transition-all"
                  style={{
                    background: i < pin.length ? "var(--rbw-navy)" : "var(--rbw-border)",
                    transform: i < pin.length ? "scale(1.2)" : "scale(1)",
                  }}
                />
              ))}
            </div>

            <input ref={inputRef} type="text" className="opacity-0 absolute" onKeyDown={handleKeyDown} readOnly />

            {showNote ? (
              <textarea
                placeholder="Note for Patrick — why late/early, forgot a punch, etc."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                maxLength={500}
                rows={2}
                autoFocus
                className="w-full px-3 py-2 mb-3 rounded-xl outline-none text-sm resize-none"
                style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg)", color: "var(--rbw-navy)" }}
              />
            ) : (
              <button
                onClick={() => setShowNote(true)}
                className="text-xs font-semibold mb-3 underline"
                style={{ color: "var(--rbw-gray-dark)" }}
              >
                + Add a note for Patrick (optional)
              </button>
            )}

            {error && (
              <p className="text-xs font-bold mb-3" style={{ color: "var(--rbw-danger)" }}>{error}</p>
            )}
            {loading && (
              <p className="text-xs font-medium mb-3" style={{ color: "var(--rbw-blue)" }}>One sec...</p>
            )}

            <div className="grid grid-cols-3 gap-2 mb-4">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", ""].map((digit, i) => {
                if (digit === "" && i === 9) return <div key={i} />;
                if (digit === "" && i === 11) {
                  return (
                    <button
                      key="backspace"
                      onClick={handleBackspace}
                      className="py-3 rounded-xl font-bold text-lg transition-all active:scale-95"
                      style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
                    >
                      &larr;
                    </button>
                  );
                }
                return (
                  <button
                    key={digit}
                    onClick={() => handleDigit(digit)}
                    disabled={loading}
                    className="py-3 rounded-xl font-bold text-xl transition-all active:scale-95 disabled:opacity-50"
                    style={{ background: "var(--rbw-bg)", color: "var(--rbw-navy)", border: "2px solid var(--rbw-border)" }}
                  >
                    {digit}
                  </button>
                );
              })}
            </div>

            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95"
              style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-gray-dark)" }}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
